/**
 * ConsumiPage — gli strumenti a canone di tutte le aziende, col consumo del periodo in corso (atlante cap. 251,
 * tappa 4). Risponde a una domanda: «chi sta consumando, e cosa arriverà in fattura?». In cima chi ha più extra in
 * fattura, poi chi consuma di più. Il clic porta alla scheda «Strumenti» dell'azienda.
 *
 * Dal 30/10/2026 (cap. 452) anche «quanto ci costa»: accanto a ciò che arriva in fattura, il costo vero dello stesso
 * periodo (AI di ogni risposta + canale riletto dal fornitore) e il margine, perché resti sempre ampio. Pagina della
 * sola piattaforma: i nostri costi non si mostrano mai a un cliente.
 *
 * Prende il posto dei «Crediti» quando i crediti spariscono (tappa 5): fino ad allora convivono.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../theme';
import { SoftChip } from '../components/SoftChip';
import { MetricNumber } from '../components/design/MetricNumber';
import { BarraQuota } from '../components/strumenti/BarraQuota';
import { comePaga, data, eurDaCentesimi, testoTetto, ultimoGiorno } from '../components/strumenti/testiStrumenti';
import { leggiConsumiDiTutte, type RigaConsumoAzienda } from '../services/strumentiService';
import { extraInFattura } from '@shared/strumenti/consumoDellAzienda';
import { margineDelPeriodo, type LivelloDelMargine } from '@shared/strumenti/margine';
import type { SoftChipTone } from '../components/SoftChip';
import { tettoRaggiunto } from '@shared/strumenti/tetto';

export const ConsumiPage: React.FC = () => {
  const navigate = useNavigate();
  const [righe, setRighe] = useState<RigaConsumoAzienda[] | null>(null);
  const [eurPerUsd, setEurPerUsd] = useState<number | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const ora = useMemo(() => Date.now(), []);

  useEffect(() => {
    leggiConsumiDiTutte(ora)
      .then((r) => { setRighe(r.righe); setEurPerUsd(r.eurPerUsd); })
      .catch((e) => setErrore(e instanceof Error ? e.message : String(e)));
  }, [ora]);

  /** Il margine di ogni riga che ha costi registrati (null: niente registrato, o manca il cambio). */
  const margine = (r: RigaConsumoAzienda) => (r.costi && eurPerUsd ? margineDelPeriodo(r.riga, r.costi, eurPerUsd) : null);

  const ordinate = useMemo(() => [...(righe ?? [])].sort((a, b) => {
    const fa = extraInFattura(a.riga) ? a.riga.extra_centesimi : 0;
    const fb = extraInFattura(b.riga) ? b.riga.extra_centesimi : 0;
    return fb - fa || b.riga.usate / Math.max(1, b.riga.quota) - a.riga.usate / Math.max(1, a.riga.quota)
      || a.tenant.nome_azienda.localeCompare(b.tenant.nome_azienda);
  }), [righe]);

  const totali = useMemo(() => {
    const r = righe ?? [];
    return {
      aziende: new Set(r.map((x) => x.tenant.id)).size,
      inFattura: r.reduce((s, x) => s + (extraInFattura(x.riga) ? x.riga.extra_centesimi : 0), 0),
      alTetto: r.filter((x) => extraInFattura(x.riga) && tettoRaggiunto(x.riga.strumento.consumo, x.riga.usate, x.tetto)).length,
      ...r.reduce((t, x) => {
        const m = x.costi && eurPerUsd ? margineDelPeriodo(x.riga, x.costi, eurPerUsd) : null;
        return m ? { costo: t.costo + m.costo_centesimi, incassoMisurato: t.incassoMisurato + m.incasso_centesimi } : t;
      }, { costo: 0, incassoMisurato: 0 }),
    };
  }, [righe, eurPerUsd]);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 600, fontSize: { xs: '1.6rem', md: '1.95rem' }, lineHeight: 1.1, letterSpacing: '-0.02em', color: tokens.color.graphite, fontVariationSettings: '"WONK" 0, "SOFT" 30' }}>
          Consumi
        </Typography>
        <Typography variant="body2" sx={{ color: tokens.color.ash, mt: 0.5, maxWidth: 600 }}>
          Centralino e agente WhatsApp di ogni azienda nel periodo di fatturazione in corso. In cima chi ha più extra in
          fattura. Il costo è quanto spendiamo noi nello stesso periodo (AI e canale), il margine è su ciò che arriva in
          fattura. Prezzi e regole si cambiano da Pricing, il regalo e i dettagli dalla scheda dell’azienda.
        </Typography>
      </Box>

      {errore && <Alert severity="error" sx={{ mb: 2 }}>{errore}</Alert>}

      <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 5 }}>
          <MetricNumber value={totali.inFattura / 100} decimals={2} unit="€" label="Extra in fattura finora" display caption="oltre ai canoni, in questo periodo" />
          <MetricNumber value={totali.aziende} label="Aziende con strumenti accesi" />
          <MetricNumber value={totali.alTetto} label="Al tetto di spesa" />
          <MetricNumber
            value={totali.costo / 100}
            decimals={2}
            unit="€"
            label="Quanto ci costano"
            caption={totali.incassoMisurato > 0
              ? `margine ${Math.round(((totali.incassoMisurato - totali.costo) / totali.incassoMisurato) * 100)}% su ${eurDaCentesimi(totali.incassoMisurato)} in fattura`
              : 'gli strumenti misurati, in questo periodo'}
          />
        </Stack>
        {righe !== null && eurPerUsd === null && (
          <Alert severity="warning" sx={{ mt: 2 }}>Il registro dei costi non ha il cambio dollaro-euro: il margine non si calcola.</Alert>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        {righe === null ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : ordinate.length === 0 ? (
          <Typography sx={{ p: 4, color: tokens.color.ash }}>Nessuna azienda ha uno strumento a canone acceso.</Typography>
        ) : (
          <Table size="small" sx={{ minWidth: 1020 }}>
            <TableHead>
              <TableRow>
                <TableCell>Azienda</TableCell>
                <TableCell>Strumento</TableCell>
                <TableCell>Come lo paga</TableCell>
                <TableCell>Consumo</TableCell>
                <TableCell align="right">Extra</TableCell>
                <TableCell>Costo e margine</TableCell>
                <TableCell>Tetto</TableCell>
                <TableCell>Periodo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ordinate.map((r) => {
                const { tenant, riga, tetto, periodo } = r;
                const paga = comePaga(riga);
                const m = margine(r);
                const inFattura = extraInFattura(riga);
                const alTetto = inFattura && tettoRaggiunto(riga.strumento.consumo, riga.usate, tetto);
                return (
                  <TableRow key={`${tenant.id}-${riga.strumento.capability}`} hover sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/tenants/${tenant.id}?tab=strumenti`)}>
                    <TableCell><Typography variant="body2" fontWeight={600}>{tenant.nome_azienda}</Typography></TableCell>
                    <TableCell>{riga.strumento.etichetta}</TableCell>
                    <TableCell><SoftChip label={paga.testo} tone={paga.tono} /></TableCell>
                    <TableCell><BarraQuota usate={riga.usate} quota={riga.quota} unitaDiConsumo={riga.strumento.consumo.unita} larghezza={180} tettoEur={tetto.tipo === 'nessuno' ? null : tetto.eur} /></TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.font.mono, fontVariantNumeric: 'tabular-nums', color: inFattura ? tokens.color.graphite : tokens.color.mist }}>
                      {riga.extra_centesimi > 0 ? eurDaCentesimi(riga.extra_centesimi) : '—'}
                      {riga.extra_centesimi > 0 && !inFattura && <Typography variant="caption" display="block">non in fattura</Typography>}
                    </TableCell>
                    <TableCell>
                      <CostoEMargine margine={m} costi={r.costi} eurPerUsd={eurPerUsd} />
                    </TableCell>
                    <TableCell>
                      {inFattura ? (
                        <Typography variant="body2" sx={{ color: alTetto ? tokens.color.danger : tokens.color.ash, fontWeight: alTetto ? 600 : 400 }}>
                          {alTetto ? 'raggiunto: ' : ''}{testoTetto(tetto, riga.strumento)}
                        </Typography>
                      ) : <Typography variant="body2" sx={{ color: tokens.color.mist }}>—</Typography>}
                    </TableCell>
                    <TableCell sx={{ color: tokens.color.ash, whiteSpace: 'nowrap' }}>
                      {periodo ? `${data(periodo.inizio_ms)} – ${ultimoGiorno(periodo.fine_ms)}` : 'niente ancora'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
};

/** Come si mostra ogni livello del margine: verde se ampio, poi via via più acceso. Le chiavi vengono dalla regola. */
const MARGINE: Record<LivelloDelMargine, { tono: SoftChipTone; testo: (quota: number | null) => string }> = {
  ampio: { tono: 'success', testo: (q) => `margine ${Math.round((q ?? 0) * 100)}%` },
  da_guardare: { tono: 'info', testo: (q) => `margine ${Math.round((q ?? 0) * 100)}%` },
  stretto: { tono: 'warning', testo: (q) => `margine stretto, ${Math.round((q ?? 0) * 100)}%` },
  in_perdita: { tono: 'error', testo: () => 'in perdita' },
  senza_incasso: { tono: 'default', testo: () => 'non pagato: costo nostro' },
};

/**
 * Il costo vero del periodo e il margine su ciò che arriva in fattura. Sotto, di che cosa è fatto il costo (AI e
 * canale), perché un margine che scende si capisce da lì.
 */
const CostoEMargine: React.FC<{ margine: ReturnType<typeof margineDelPeriodo> | null; costi: RigaConsumoAzienda['costi']; eurPerUsd: number | null }> = ({ margine, costi, eurPerUsd }) => {
  if (!margine || !costi || !eurPerUsd) {
    return <Typography variant="body2" sx={{ color: tokens.color.mist }}>{costi ? '—' : 'non misurato'}</Typography>;
  }
  const eur = (usd: number) => eurDaCentesimi(Math.ceil(usd * eurPerUsd * 100));
  const stile = MARGINE[margine.livello];
  const parti = [
    `AI ${eur(costi.ai_usd)}`,
    ...(costi.ai_prova_usd > 0 ? [`prove ${eur(costi.ai_prova_usd)}`] : []),
    costi.canale_usd === null ? 'canale da rileggere' : `canale ${eur(costi.canale_usd)}`,
  ];
  return (
    <Box sx={{ minWidth: 170 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" sx={{ fontFamily: tokens.font.mono, fontVariantNumeric: 'tabular-nums', color: tokens.color.graphite }}>
          {eurDaCentesimi(margine.costo_centesimi)}
        </Typography>
        <SoftChip label={stile.testo(margine.quota)} tone={stile.tono} />
      </Stack>
      <Typography variant="caption" display="block" sx={{ color: tokens.color.ash, mt: 0.25 }}>
        {parti.join(', ')}{costi.ai_prezzo_prudente ? ' (un modello fuori listino, contato al prezzo prudente)' : ''}
      </Typography>
    </Box>
  );
};

export default ConsumiPage;
